import React, { useState, useEffect } from 'react';
import { useApp } from '../../context/AppContext';
import { useTranslations } from '../../context/TranslationsContext';
import { whatsappAPI } from '../../api';

const ConversationList = ({ onConversationSelect, selectedConversation }) => {
  const { state } = useApp();
  const { translations } = useTranslations();
  const t = translations[state.currentLanguage];

  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchConversations = async () => {
      if (!state.token) return;
      
      setLoading(true);
      try {
        console.log('Fetching unique conversations...');
        const uniqueConversations = await whatsappAPI.getUniqueConversations(state.token);
        console.log('Received conversations:', uniqueConversations);
        
        if (!uniqueConversations || uniqueConversations.length === 0) {
          console.log('No conversations found');
          setConversations([]);
          return;
        }
        
        const conversationsData = uniqueConversations.map((conversation) => {
          return {
            id: conversation.session_id,
            phoneNumber: conversation.phone_number,
            customerName: conversation.customer_name,
            isCustomer: conversation.is_customer,
            customerId: conversation.customer_id,
            step: conversation.step,
            lastMessage: conversation.message_content || 'لا توجد رسائل',
            lastMessageTime: conversation.timestamp,
            unreadCount: 0,
            isActive: !['ended', 'completed'].includes(conversation.step),
            messageCount: conversation.message_count || 0,
            messageType: conversation.message_type
          };
        });
        
        console.log('Processed conversations:', conversationsData);
        setConversations(conversationsData.sort((a, b) => 
          new Date(b.lastMessageTime) - new Date(a.lastMessageTime)
        ));
      } catch (error) {
        console.error('Error fetching conversations:', error);
        setConversations([]);
      } finally {
        setLoading(false);
      }
    };

    fetchConversations();
  }, [state.token]);

  const filteredConversations = conversations.filter(conv =>
    conv.phoneNumber.includes(searchTerm) ||
    conv.lastMessage.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);
    
    if (diffInHours < 24) {
      return date.toLocaleTimeString('ar-SA', { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    } else if (diffInHours < 168) {
      return date.toLocaleDateString('ar-SA', { 
        weekday: 'short' 
      });
    } else {
      return date.toLocaleDateString('ar-SA', { 
        day: 'numeric', 
        month: 'short' 
      });
    }
  };

  const getInitials = (phoneNumber) => {
    return phoneNumber.slice(-2);
  };

  if (loading) {
    return (
      <div className="conversation-list">
        <div className="conversation-list-header">
          <h3>المحادثات</h3>
        </div>
        <div className="conversation-list-search">
          <input
            type="text"
            placeholder="البحث في المحادثات..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="conversation-search-input"
          />
        </div>
        <div className="conversation-list-content">
          <div className="conversation-loading">جاري تحميل المحادثات...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="conversation-list">
      <div className="conversation-list-header">
        <h3>المحادثات</h3>
      </div>
      
      <div className="conversation-list-search">
        <input
          type="text"
          placeholder="البحث في المحادثات..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="conversation-search-input"
        />
      </div>
      
      <div className="conversation-list-content">
        {filteredConversations.length === 0 ? (
          <div className="no-conversations">
            <div className="no-conversations-icon">💬</div>
            <p>لا توجد محادثات</p>
            <p className="no-conversations-subtitle">
              {searchTerm ? 'لم يتم العثور على محادثات تطابق البحث' : 'لم يتم العثور على محادثات في قاعدة البيانات'}
            </p>
          </div>
        ) : (
          filteredConversations.map((conversation) => (
            <div
              key={conversation.id}
              className={`conversation-item ${selectedConversation?.id === conversation.id ? 'selected' : ''}`}
              onClick={() => onConversationSelect(conversation)}
            >
              <div className="conversation-avatar">
                <div className="avatar-circle">
                  {getInitials(conversation.phoneNumber)}
                </div>
                {conversation.isActive && <div className="online-indicator"></div>}
              </div>
              
              <div className="conversation-content">
                <div className="conversation-header">
                  <div className="conversation-name">
                    {conversation.isCustomer ? (
                      <div className="customer-info">
                        <div className="customer-name">{conversation.customerName}</div>
                        <div className="phone-number">{conversation.phoneNumber}</div>
                      </div>
                    ) : (
                      <div className="phone-only">{conversation.phoneNumber}</div>
                    )}
                  </div>
                  <div className="conversation-time">
                    {formatTime(conversation.lastMessageTime)}
                  </div>
                </div>
                
                <div className="conversation-preview">
                  <div className="conversation-message">
                    {conversation.lastMessage}
                  </div>
                  <div className="conversation-meta">
                    {conversation.messageCount > 0 && (
                      <span className="message-count">
                        {conversation.messageCount} رسالة
                      </span>
                    )}
                    {conversation.unreadCount > 0 && (
                      <div className="unread-badge">
                        {conversation.unreadCount}
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="conversation-step">
                  <span className="step-badge">{conversation.step}</span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ConversationList;
